import * as React from 'react'

import {
  Box,
  Button,
  Table,
  Text,
  Touchable,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { OverflowContextProvider } from 'modules/core/lib/overflowContext'

import CompetencyInfo from 'modules/competencies/components/CompetencyInfo'
import type { Assessment } from 'modules/competencies/store/models/Assessment'
import type { Competency } from 'modules/competencies/store/models/Competency'
import type { CompetencyOption } from 'modules/competencies/store/models/CompetencyOption'

import AddComment from '../_AddComment'

interface CompetencyRowProps {
  assessment?: Assessment
  competency: Competency
  isParent?: boolean
  canEdit: boolean
  isDraft: boolean
}

const CompetencyRow = observer(
  ({ assessment, competency, isParent = false, canEdit, isDraft }: CompetencyRowProps) => {
    const [comment, setComment] = React.useState<string>(assessment?.attributes.description || '')
    const [commentModalOpen, toggleCommentModal] = useToggle(false)

    const { performAction: updateAssessmentValue } = useAsyncAction((option: CompetencyOption) => {
      if (!assessment) return Promise.resolve()

      return assessment.save({ competency_option_id: option.attributes.id })
    })
    const canAccess = canEdit || comment.length > 0
    const ref = React.useRef(null)

    const assertCellEllipsis = (text: string, maxCharacters = 15): boolean => {
      const wordsArr = text.split(' ').map((word) => word.length)
      const isLongerThanMaxChar = (letterCount: number) => letterCount > maxCharacters

      return wordsArr.some(isLongerThanMaxChar)
    }

    // NOTE: This weird colSpan is done to fit all possible options [0..10] = 11 in a row
    return (
      <Table.Row background={isParent ? 'grey000White' : 'grey100'}>
        <Table.Cell colSpan={7} noBorder={isParent}>
          <Box
            flexDirection="row"
            gap="s12"
            alignItems="center"
            aria-label="assessment-competency-name"
          >
            {assertCellEllipsis(competency.attributes.name) ? (
              <OverflowContextProvider childrenRef={ref} value={competency.attributes.name}>
                <Text.Normal weight="medium" innerRef={ref} ellipsis>
                  {competency.attributes.name}
                </Text.Normal>
              </OverflowContextProvider>
            ) : (
              <Text.Normal weight="medium" innerRef={ref}>
                {competency.attributes.name}
              </Text.Normal>
            )}
            <CompetencyInfo competency={competency} />
          </Box>
        </Table.Cell>
        <Table.Cell colSpan={16} noBorder={isParent}>
          {assessment && (
            <Box flexDirection="row" gap="s4" aria-label="assessment-competency-rating">
              {assessment.competency.options.map((option) => {
                const isSelected = assessment?.option?.attributes.id === option.attributes.id

                return (
                  <>
                    {isDraft && (
                      <Touchable
                        onClick={() => updateAssessmentValue(option)}
                        key={option.attributes.id}
                        disabled={!canEdit}
                      >
                        <Box
                          width="s40"
                          height="s32"
                          justifyContent="center"
                          borderRadius={{ all: 'abs008' }}
                          background={isSelected ? 'viridian1000' : 'lighter000'}
                        >
                          <Text.Normal size="200">{option.attributes.value}</Text.Normal>
                        </Box>
                      </Touchable>
                    )}
                    {!isDraft && (
                      <Box
                        width="s40"
                        height="s32"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius={{ all: 'abs008' }}
                        background={isSelected ? 'grey800' : 'lighter000'}
                      >
                        <Text.Normal size="200" color={isSelected ? 'grey000White' : undefined}>
                          {option.attributes.value}
                        </Text.Normal>
                      </Box>
                    )}
                  </>
                )
              })}
            </Box>
          )}
        </Table.Cell>
        <Table.Cell colSpan={2} noBorder={isParent}>
          {assessment && canAccess && (
            <Box
              flexDirection="row"
              justifyContent="flexEnd"
              aria-label="assessment-competency-comment"
            >
              <Button
                type="secondary"
                onClick={toggleCommentModal(true)}
                iconName={assessment.attributes.description ? 'file-filled' : 'file'}
              ></Button>
              {commentModalOpen && (
                <AddComment
                  assessment={assessment}
                  onClose={toggleCommentModal(false)}
                  comment={comment}
                  setComment={setComment}
                  canEdit={canEdit}
                  isDraft={isDraft}
                />
              )}
            </Box>
          )}
        </Table.Cell>
      </Table.Row>
    )
  }
)

interface Props {
  parentAssessment?: Assessment
  childAssessments: Assessment[]
  canEdit: boolean
  isDraft: boolean
}

const AssessmentTable = ({ parentAssessment, childAssessments, canEdit, isDraft }: Props) => {
  const parentCompetency = parentAssessment?.competency || childAssessments.at(0)?.competency.parent

  if (!parentCompetency) {
    throw new Error(
      `No parent competency found! First child id: ${childAssessments.at(0)?.competency.id}`
    )
  }

  return (
    <Table>
      <Table.Body>
        <CompetencyRow
          assessment={parentAssessment}
          competency={parentCompetency}
          isParent
          canEdit={canEdit}
          isDraft={isDraft}
        />
        {childAssessments.map((assessment) => (
          <CompetencyRow
            key={assessment.attributes.id}
            assessment={assessment}
            competency={assessment.competency}
            canEdit={canEdit}
            isDraft={isDraft}
          />
        ))}
      </Table.Body>
    </Table>
  )
}

export default observer(AssessmentTable)
